import {
  AfterContentInit,
  Component,
  ContentChildren, ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {DropdownSelectOptionComponent} from '../dropdown-select-option/dropdown-select-option.component';
import {NgClass} from '@angular/common';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {createPopper, Instance} from '@popperjs/core';

@Component({
  selector: 'app-dropdown-select',
  imports: [
    NgClass,
    FormsModule
  ],
  templateUrl: './dropdown-select.component.html',
  styleUrl: './dropdown-select.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownSelectComponent),
      multi: true
    }
  ]
})
export class DropdownSelectComponent implements ControlValueAccessor, AfterContentInit {

  @Input() multiple = false;
  @Input() selectAll = false;
  @Input() formControlName: string = '';
  @Input() placeholder: string = '';
  @Input() showBadge = true;

  @Output() onDropdownChange = new EventEmitter();

  @ContentChildren(DropdownSelectOptionComponent) options!: QueryList<DropdownSelectOptionComponent>;

  @ViewChild('dropdownToggle') dropdownToggle!: ElementRef<HTMLElement>;
  @ViewChild('dropdownSelect') dropdownSelect!: ElementRef<HTMLElement>;

  private popper?: Instance;
  private changed = false;
  private shown = false;
  private ignoreClick = false;
  private selectedOptions: DropdownSelectOptionComponent[] = [];
  private values: any[] | null = null;
  private value: any | null = null;
  searchTerm = '';
  selectedOptionsHtml: SafeHtml[] = [];
  activeIndex: number = -1;
  hoveredIndex: number = -1;

  onChange: any = () => {
  }

  constructor(
    private sanitizer: DomSanitizer,
  ) {
  }

  onSelectAll(): void {
    if (!this.multiple) {
      return;
    }

    const options = this.options.toArray();

    const checked: boolean = !options[0].isChecked();

    options.forEach((option: DropdownSelectOptionComponent) => {
      this.onOptionChange(option.value, checked);
    });
  }

  onOptionChange(value: any, checked: boolean): void {
    this.changed = true;

    if (this.multiple) {
      // Multiple values
      const values = Array.isArray(this.values) ? this.values.filter(v => v != value) : [];

      if (checked) {
        values.push(value);
      }

      this.values = values;

      this.onChange(this.values);
    } else {
      // Single value
      this.value = checked ? value : null;
      this.values = [this.value];
      this.onChange(this.value);
      this.hide();
    }

    this.updateSelectedOptions();
  }

  getTemplate(option: DropdownSelectOptionComponent): SafeHtml {
    const html = option.getElement()?.nativeElement.innerHTML;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  writeValue(obj: any): void {
    if (this.multiple) {
      this.values = obj;
    } else {
      this.values = [obj];
      this.value = obj;
    }

    this.updateSelectedOptions();
  }

  isChecked(optionValue: any): boolean {
    if (this.values) {
      for (let value of this.values) {
        if (optionValue === value) {
          return true;
        }
      }
    }

    return false;
  }

  updateSelectedOptions(): void {
    this.selectedOptions = [];
    this.selectedOptionsHtml = [];

    if (this.options) {
      this.options.toArray().forEach((option: DropdownSelectOptionComponent) => {
        if (option && Array.isArray(this.values) && this.values?.findIndex(value => value === option.value) >= 0) {
          this.selectedOptions.push(option);
          this.selectedOptionsHtml.push(this.getTemplate(option));
        }
      });
    }
  }

  getSelectedOptions(): DropdownSelectOptionComponent[] {
    return this.selectedOptions;
  }

  hasPlaceholder(): boolean {
    return this.placeholder?.length > 0;
  }

  isShown(): boolean {
    return this.shown;
  }

  toggle(): void {
    if (this.isShown()) {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    this.shown = true;
    this.changed = false;

    this.popper = createPopper(
      this.dropdownToggle.nativeElement,
      this.dropdownSelect.nativeElement,
      {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [-12, 0]
            }
          }
        ]
      }
    );
  }

  hide(): void {
    if (this.shown) {
      this.shown = false;
      this.clearSearch();

      if (this.changed) {
        this.onDropdownChange.emit();
      }
    }
  }

  @HostListener('body:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.shown) {
      return;
    }

    const key = event.key;
    const optionArray = this.options.toArray();

    if (key === 'ArrowDown') {
      event.preventDefault();

      if (this.activeIndex < optionArray.length - 1) {
        this.setActiveIndex(this.activeIndex + 1);
      } else {
        this.setActiveIndex(0);
      }

      this.scrollToActive();
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();

      if (this.activeIndex > 0) {
        this.setActiveIndex(this.activeIndex - 1);
      } else {
        this.setActiveIndex(optionArray.length - 1);
      }

      this.scrollToActive();
      return;
    }

    if (key === 'Enter') {
      const optionArray = this.options.toArray();
      if (this.activeIndex >= 0 && optionArray[this.activeIndex]) {
        const opt = optionArray[this.activeIndex];
        const alreadySelected = this.isChecked(opt.value);
        this.onOptionChange(opt.value, !alreadySelected);
      }
      return;
    }

    if (key === 'Escape') {
      if (this.searchTerm) {
        this.clearSearch();
      } else {
        this.hide();
      }
      return;
    }

    if (key.length === 1 && !event.ctrlKey && !event.metaKey) {
      this.searchTerm += key.toLowerCase();
    }

    if (key === 'Backspace') {
      if (this.searchTerm.length) {
        this.searchTerm = this.searchTerm.substring(0, this.searchTerm.length - 1);
      }
    }
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  setActiveIndex(index: number): void {
    this.activeIndex = index;

    this.options.forEach(opt => opt.updateActiveState());
  }

  setHoveredIndex(index: number): void {
    this.hoveredIndex = index;
    this.activeIndex = -1;
    this.options.forEach(opt => opt.updateActiveState());
  }

  private scrollToActive(): void {
    const option = this.options.toArray()[this.activeIndex];
    if (option) {
      const el = option.getElement()?.nativeElement;
      el?.scrollIntoView({block: 'nearest'});
    }
  }

  @HostListener('click')
  onDropdownClick(): void {
    this.ignoreClick = true;
  }

  @HostListener('body:click')
  onBodyClick(): void {
    if (!this.ignoreClick) {
      this.hide();
    }
    this.ignoreClick = false;
  }

  ngAfterContentInit(): void {
    this.updateSelectedOptions();

    // Options may be rendered after the value has been written (async loaded lists)
    this.options.changes.subscribe(() => setTimeout(() => this.updateSelectedOptions()));
  }

  hasNoOptions(): boolean {
    for (let option of this.options) {
      if (!option.isFiltered()) {
        return false;
      }
    }

    return true;
  }
}
